import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { KeyRound, Pencil, Users } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatArea, formatDate, formatNumber, hasPermission, OWNERSHIP_PARTY_ROLE_LABELS, TENANCY_PARTY_ROLE_LABELS } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  DescriptionList,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@blimo/ui';
import { PaymentTimeline } from '@blimo/ui/finance';
import { DetailSkeleton, ExternalReferences, PartyList, PropertyLink } from '../../components/data.jsx';
import { UnitEditSheet } from './edit-sheet.jsx';
import { DocumentManager } from '../../components/documents/document-manager.jsx';

/** Tabs der Einheit; `dokumente` nur mit `documents.view`. */
const UNIT_TABS = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'dokumente', label: 'Dokumente', permission: 'documents.view' },
];

const period = (from, to) => `${from ? formatDate(from) : 'unbekannt'} – ${to ? formatDate(to) : 'heute'}`;

export function UnitDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const [editOpen, setEditOpen] = useState(false);
  const { unitId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data: unit, error, isPending, refetch } = useQuery(adminQueries.unit(api, unitId));
  const breadcrumb = (
    <Link to="/einheiten" className="hover:underline">
      Einheiten
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Einheit" breadcrumb={breadcrumb} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Einheit nicht gefunden"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/einheiten">Zur Liste</Link>
            </Button>
          }
        />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const canEdit = hasPermission(me, 'units.update');
  const visibleTabs = UNIT_TABS.filter((t) => !t.permission || hasPermission(me, t.permission));
  const tab = visibleTabs.some((t) => t.id === search.tab) ? search.tab : 'uebersicht';
  const setTab = (next) => navigate({ to: '/einheiten/$unitId', params: { unitId }, search: { tab: next === 'uebersicht' ? undefined : next }, replace: true });
  const ownerships = unit.ownerships ?? [];
  const current = ownerships.filter((o) => o.is_current);
  const historical = ownerships.filter((o) => !o.is_current);

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={[unit.number, unit.designation].filter(Boolean).join(' · ') || `Einheit #${unit.id}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <PropertyLink property={unit.property} />
            {unit.type?.label && <Badge variant="secondary">{unit.type.label}</Badge>}
            {unit.archived_at && <Badge variant="neutral">Archiviert</Badge>}
          </span>
        }
        actions={
          canEdit && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil aria-hidden="true" /> Bearbeiten
            </Button>
          )
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Bereiche der Einheit">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="uebersicht">
          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle as="h2">Stammdaten</CardTitle>
                <CardDescription>Nummer, Bezeichnung und Fläche stammen aus WinCasa; Notizen werden in BLIMO gepflegt.</CardDescription>
              </CardHeader>
              <CardContent>
                <DescriptionList
                  items={[
                    { label: 'Nummer', value: unit.number, hint: 'aus WinCasa' },
                    { label: 'Bezeichnung', value: unit.designation, hint: 'aus WinCasa' },
                    { label: 'Typ', value: unit.type?.label },
                    { label: 'Typ laut Quelle', value: unit.source_type_label },
                    { label: 'Wohnfläche', value: unit.living_space != null ? formatArea(unit.living_space) : null },
                    { label: 'Ausstattung', value: unit.furnishing },
                    { label: 'Notizen', value: unit.notes ? <span className="whitespace-pre-line">{unit.notes}</span> : null },
                  ]}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle as="h2">Externe Schlüssel</CardTitle>
              </CardHeader>
              <CardContent>
                <ExternalReferences references={unit.external_references} />
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle as="h2">Eigentum</CardTitle>
                <CardDescription>
                  {current.length} aktuell · {historical.length} historisch
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ownerships.length === 0 ? (
                  <EmptyState compact icon={Users} title="Keine Eigentumsverhältnisse erfasst" />
                ) : (
                  <ol className="flex flex-col" aria-label="Verlauf der Eigentumsverhältnisse">
                    {ownerships.map((o) => (
                      <TimelineItem key={o.id} active={o.is_current}>
                        <div className="flex flex-wrap items-center gap-2">
                          {o.is_current ? <Badge variant="success">Aktuell</Badge> : <Badge variant="neutral">Historisch</Badge>}
                          <span className="numeric text-sm">{period(o.valid_from, o.valid_to)}</span>
                          {o.account_number && <span className="numeric text-xs text-muted-foreground">Konto {o.account_number}</span>}
                          {o.share && (
                            <span className="numeric text-xs text-muted-foreground">
                              Anteil {formatNumber(o.share.numerator)}/{formatNumber(o.share.denominator)}
                            </span>
                          )}
                          {o.is_owner_occupier && <Badge variant="outline">Selbstnutzer</Badge>}
                        </div>
                        <div className="mt-1 text-sm">
                          <PartyList parties={o.parties} roleLabels={OWNERSHIP_PARTY_ROLE_LABELS} />
                        </div>
                      </TimelineItem>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle as="h2">Mietverhältnisse</CardTitle>
                <CardDescription>Quelle: Buchhaltung (nur lesend)</CardDescription>
              </CardHeader>
              <CardContent>
                {(unit.tenancies ?? []).length === 0 ? (
                  <EmptyState compact icon={KeyRound} title="Keine Mietverhältnisse erfasst" />
                ) : (
                  <ol className="flex flex-col" aria-label="Mietverhältnisse">
                    {unit.tenancies.map((t) => (
                      <TimelineItem key={t.id} active={t.status === 'current'}>
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge domain="tenancy" status={t.status} />
                          <span className="numeric text-sm">{period(t.starts_on, t.ends_on)}</span>
                          {t.terminated_on && <span className="numeric text-xs text-muted-foreground">gekündigt am {formatDate(t.terminated_on)}</span>}
                          {hasPermission(me, 'tenancies.view') ? (
                            <Link
                              to="/mietverhaeltnisse/$tenancyId"
                              params={{ tenancyId: String(t.id) }}
                              className="numeric text-xs text-primary hover:underline"
                            >
                              Konto {t.account_number ?? `#${t.id}`} · Details &amp; Dokumente
                            </Link>
                          ) : (
                            t.account_number && <span className="numeric text-xs text-muted-foreground">Konto {t.account_number}</span>
                          )}
                        </div>
                        <div className="mt-1 text-sm">
                          <PartyList parties={t.parties} roleLabels={TENANCY_PARTY_ROLE_LABELS} />
                        </div>
                        {t.status === 'current' && hasPermission(me, 'finance.view') && <TenancyPayments tenancyId={t.id} />}
                      </TimelineItem>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="dokumente">
          <DocumentManager ownerType="unit" ownerId={unitId} title="Dokumente der Einheit" />
        </TabsContent>
      </Tabs>
      {canEdit && <UnitEditSheet unit={unit} open={editOpen} onOpenChange={setEditOpen} />}
    </>
  );
}

/**
 * Zahlungsverlauf des aktuellen Mietverhältnisses (12 Monate, `finance.view`) – Berechnung im Backend.
 * @param {{ tenancyId: number }} props
 */
function TenancyPayments({ tenancyId }) {
  const api = useApi();
  const query = useQuery(adminQueries.tenancyPaymentHistory(api, tenancyId));
  return (
    <section className="mt-3 rounded-md border bg-muted/20 p-3" aria-label="Zahlungsverlauf">
      <h3 className="mb-2 text-xs font-medium text-muted-foreground">Zahlungsverlauf (12 Monate)</h3>
      {query.error ? (
        <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <PaymentTimeline history={query.data} showRules />
      )}
    </section>
  );
}

function TimelineItem({ active, children }) {
  return (
    <li className="relative border-l pb-4 pl-5 last:pb-0">
      <span
        aria-hidden="true"
        className={cn('absolute -left-[5px] top-1.5 size-2.5 rounded-full border-2 border-card', active ? 'bg-success' : 'bg-muted-foreground/50')}
      />
      {children}
    </li>
  );
}
